/**
 * Aggregator online-availability monitor.
 *
 * Reads marketing.outlet_availability_trend (currently Swiggy only —
 * Zomato has no equivalent column). Highlights any outlet running
 * < 95% online (mild) or < 90% (severe). Drives a weekly checklist
 * item for ops triage.
 */
import { useMemo } from 'react';
import { AlertTriangle, Power, PowerOff } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useOutlets } from '@/hooks/useOutlets';
import {
  type AvailabilitySeverity,
  type AvailabilityTrendRow,
  useAvailabilityTrends,
} from '@/hooks/useAvailabilityTrends';
import { cn } from '@/lib/utils';

export function AvailabilityPanel() {
  const outlets = useOutlets();
  const trends = useAvailabilityTrends();

  const rowByOutlet = useMemo(() => {
    const map = new Map<string, AvailabilityTrendRow>();
    for (const r of trends.data ?? []) map.set(r.outlet_id, r);
    return map;
  }, [trends.data]);

  const severe = useMemo(
    () =>
      (trends.data ?? [])
        .filter((r) => r.severity === 'severe')
        .sort((a, b) => (Number(a.avail_7d) || 0) - (Number(b.avail_7d) || 0)),
    [trends.data],
  );

  // Sort outlets: severe → mild → ok → excellent → no-data (alpha within tier).
  const orderedOutlets = useMemo(() => {
    const list = outlets.data ?? [];
    const rank = (sev: AvailabilitySeverity | undefined) =>
      sev === 'severe' ? 0 : sev === 'mild' ? 1 : sev === 'ok' ? 2 : sev === 'excellent' ? 3 : 4;
    return [...list].sort((a, b) => {
      const ra = rank(rowByOutlet.get(a.id)?.severity);
      const rb = rank(rowByOutlet.get(b.id)?.severity);
      return ra - rb || a.name.localeCompare(b.name);
    });
  }, [outlets.data, rowByOutlet]);

  const hasAnyData = (trends.data ?? []).some((r) => r.avail_7d != null);
  const loading = outlets.isLoading || trends.isLoading;

  return (
    <div className="space-y-4">
      {severe.length > 0 && (
        <Card className="border-rose-500/30 bg-rose-500/5">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-rose-700 dark:text-rose-400">
              <AlertTriangle className="h-4 w-4" />
              Availability fires ({severe.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ol className="space-y-1.5 text-sm">
              {severe.slice(0, 8).map((f) => {
                const outlet = outlets.data?.find((o) => o.id === f.outlet_id);
                return (
                  <li key={f.outlet_id} className="flex items-start gap-2">
                    <PowerOff className="mt-0.5 h-3.5 w-3.5 shrink-0 text-rose-600" />
                    <span className="flex-1">
                      <strong>{outlet?.name ?? f.outlet_id}</strong>
                      <span className="ml-2 text-muted-foreground">Swiggy 7d avg</span>
                      <span className="ml-2 font-semibold text-rose-600 dark:text-rose-400 tabular-nums">
                        {Number(f.avail_7d).toFixed(1)}%
                      </span>
                      {f.delta_7d_vs_28d != null && (
                        <span className="ml-2 text-2xs text-muted-foreground tabular-nums">
                          ({Number(f.delta_7d_vs_28d) > 0 ? '+' : ''}
                          {Number(f.delta_7d_vs_28d).toFixed(1)} vs 28d)
                        </span>
                      )}
                    </span>
                  </li>
                );
              })}
            </ol>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Power className="h-4 w-4 text-emerald-500" />
            Online availability per outlet · Swiggy
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="space-y-2 p-4">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : !hasAnyData ? (
            <div className="p-6 text-sm text-muted-foreground">
              No Swiggy availability data synced yet. The panel populates as soon as
              the first Supermetrics sync writes to{' '}
              <code className="rounded bg-muted px-1 py-0.5 text-2xs">
                swiggy_daily.online_availability_pct
              </code>
              .
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] text-sm">
                <thead>
                  <tr className="border-b border-border/40 text-2xs uppercase tracking-wide text-muted-foreground">
                    <th className="px-4 py-2 text-left">Outlet</th>
                    <th className="px-3 py-2 text-right">7d avg</th>
                    <th className="px-3 py-2 text-right">28d avg</th>
                    <th className="px-3 py-2 text-right">Δ vs 28d</th>
                    <th className="px-3 py-2 text-right">Days reporting</th>
                  </tr>
                </thead>
                <tbody>
                  {orderedOutlets.map((o) => {
                    const r = rowByOutlet.get(o.id);
                    return (
                      <tr key={o.id} className="border-b border-border/30 last:border-0">
                        <td className="px-4 py-2.5">
                          <div className="font-medium">{o.name}</div>
                          {o.city && (
                            <div className="text-2xs text-muted-foreground">{o.city}</div>
                          )}
                        </td>
                        {!r ? (
                          <td colSpan={4} className="px-3 py-2.5 text-2xs text-muted-foreground">
                            no data
                          </td>
                        ) : (
                          <>
                            <td className="px-3 py-2.5 text-right">
                              <AvailCell value={r.avail_7d} severity={r.severity} showBadge />
                            </td>
                            <td className="px-3 py-2.5 text-right">
                              <AvailCell value={r.avail_28d} severity={r.severity} />
                            </td>
                            <td className="px-3 py-2.5 text-right">
                              <DeltaCell value={r.delta_7d_vs_28d} />
                            </td>
                            <td className="px-3 py-2.5 text-right text-2xs text-muted-foreground tabular-nums">
                              {r.days_with_data_7d ?? 0}/7 · {r.days_with_data_28d ?? 0}/28
                            </td>
                          </>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function AvailCell({
  value,
  severity,
  showBadge,
}: {
  value: number | null;
  severity: AvailabilitySeverity;
  showBadge?: boolean;
}) {
  if (value == null) {
    return <span className="text-2xs text-muted-foreground">—</span>;
  }
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={cn('font-semibold tabular-nums', availColor(value))}>
        {Number(value).toFixed(1)}%
      </span>
      {showBadge && <SeverityBadge severity={severity} />}
    </span>
  );
}

function DeltaCell({ value }: { value: number | null }) {
  if (value == null) return <span className="text-2xs text-muted-foreground">—</span>;
  const v = Number(value);
  const color =
    Math.abs(v) < 0.5
      ? 'text-muted-foreground'
      : v > 0
        ? 'text-emerald-600 dark:text-emerald-400'
        : 'text-rose-600 dark:text-rose-400';
  return (
    <span className={cn('tabular-nums', color)}>
      {v > 0 ? '+' : ''}
      {v.toFixed(1)}
    </span>
  );
}

function SeverityBadge({ severity }: { severity: AvailabilitySeverity }) {
  const map: Record<AvailabilitySeverity, { label: string; cls: string } | null> = {
    severe: { label: '<90%', cls: 'bg-rose-500/15 text-rose-700 dark:text-rose-400' },
    mild: { label: '<95%', cls: 'bg-amber-500/15 text-amber-700 dark:text-amber-400' },
    ok: null,
    excellent: { label: '99%+', cls: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400' },
    unknown: null,
  };
  const v = map[severity];
  if (!v) return null;
  return (
    <Badge variant="muted" className={cn('text-2xs', v.cls)}>
      {v.label}
    </Badge>
  );
}

function availColor(v: number): string {
  if (v >= 99) return 'text-emerald-700 dark:text-emerald-400';
  if (v >= 95) return 'text-foreground';
  if (v >= 90) return 'text-amber-700 dark:text-amber-400';
  return 'text-rose-700 dark:text-rose-400';
}
